import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Shield, AlertTriangle, Ban, Key, RefreshCw, Search, 
  Loader2, ChevronLeft, Activity, Users, Clock, Filter,
  AlertCircle, CheckCircle, Info
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import API from '@/utils/api';

const SecurityAuditPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedIP, setSelectedIP] = useState(null);
  const [ipDetails, setIPDetails] = useState(null);
  const [filters, setFilters] = useState({
    hours: '24',
    eventType: 'all',
    severity: 'all',
    searchIP: ''
  });

  useEffect(() => {
    fetchData();
  }, [filters.hours]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, eventsRes, alertsRes] = await Promise.all([
        API.get(`/admin/security/dashboard?hours=${filters.hours}`),
        API.get(`/admin/security/events?hours=${filters.hours}&limit=100`),
        API.get(`/admin/security/alerts?hours=${filters.hours}&limit=50`)
      ]);
      
      setDashboard(dashboardRes.data);
      setEvents(eventsRes.data.events);
      setAlerts(alertsRes.data.alerts);
    } catch (error) {
      console.error('Failed to fetch security data:', error);
      toast.error('Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const fetchIPDetails = async (ip) => {
    setSelectedIP(ip);
    try {
      const response = await API.get(`/admin/security/ip/${ip}?hours=${filters.hours}`);
      setIPDetails(response.data);
    } catch (error) {
      toast.error('Failed to load IP details');
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      warning: 'bg-amber-100 text-amber-700 border-amber-200',
      info: 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return (
      <Badge variant="outline" className={styles[severity] || styles.info}>
        {severity}
      </Badge>
    );
  };

  const getEventIcon = (eventType) => {
    const icons = {
      failed_login: <Key className="h-4 w-4 text-red-500" />,
      rate_limit_exceeded: <Ban className="h-4 w-4 text-amber-500" />,
      password_reset_requested: <RefreshCw className="h-4 w-4 text-blue-500" />,
      brute_force_detected: <AlertTriangle className="h-4 w-4 text-red-600" />,
      suspicious_activity: <AlertCircle className="h-4 w-4 text-amber-600" />
    };
    return icons[eventType] || <Activity className="h-4 w-4 text-gray-500" />;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const filteredEvents = events.filter(event => {
    if (filters.eventType !== 'all' && event.event_type !== filters.eventType) return false;
    if (filters.severity !== 'all' && event.severity !== filters.severity) return false;
    if (filters.searchIP && !event.ip_address?.includes(filters.searchIP)) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold flex items-center gap-2">
                  <Shield className="h-5 w-5 text-green-600" />
                  Security Audit Log
                </h1>
                <p className="text-sm text-gray-500">Monitor security events and alerts</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={filters.hours} onValueChange={(v) => setFilters(f => ({...f, hours: v}))}>
                <SelectTrigger className="w-32">
                  <Clock className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last hour</SelectItem>
                  <SelectItem value="6">Last 6 hours</SelectItem>
                  <SelectItem value="24">Last 24 hours</SelectItem>
                  <SelectItem value="72">Last 3 days</SelectItem>
                  <SelectItem value="168">Last 7 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Events</p>
                  <p className="text-2xl font-bold">{dashboard?.summary?.total_events || 0}</p>
                </div>
                <Activity className="h-8 w-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Failed Logins</p>
                  <p className="text-2xl font-bold text-red-600">{dashboard?.summary?.failed_logins || 0}</p>
                </div>
                <Key className="h-8 w-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Rate Limit Violations</p>
                  <p className="text-2xl font-bold text-amber-600">{dashboard?.summary?.rate_limit_violations || 0}</p>
                </div>
                <Ban className="h-8 w-8 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={dashboard?.summary?.critical_alerts > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Critical Alerts</p>
                  <p className={`text-2xl font-bold ${dashboard?.summary?.critical_alerts > 0 ? 'text-red-600' : ''}`}>
                    {dashboard?.summary?.critical_alerts || 0}
                  </p>
                </div>
                <AlertTriangle className={`h-8 w-8 ${dashboard?.summary?.critical_alerts > 0 ? 'text-red-500' : 'text-gray-400'} opacity-50`} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Suspicious IPs */}
        {dashboard?.top_ips?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                Top IPs by Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {dashboard.top_ips.slice(0, 10).map((item) => (
                  <Button
                    key={item.ip}
                    variant="outline"
                    size="sm"
                    onClick={() => fetchIPDetails(item.ip)}
                    className="font-mono text-xs"
                  >
                    {item.ip}
                    <Badge variant="secondary" className="ml-2">{item.count}</Badge>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="events">
          <TabsList>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Events ({filteredEvents.length})
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Alerts ({alerts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="mt-4">
            {/* Filters */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium">Filters:</span>
                  </div>
                  
                  <Select 
                    value={filters.eventType} 
                    onValueChange={(v) => setFilters(f => ({...f, eventType: v}))}
                  >
                    <SelectTrigger className="w-44">
                      <SelectValue placeholder="Event Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Events</SelectItem>
                      <SelectItem value="failed_login">Failed Logins</SelectItem>
                      <SelectItem value="rate_limit_exceeded">Rate Limits</SelectItem>
                      <SelectItem value="password_reset_requested">Password Resets</SelectItem>
                      <SelectItem value="brute_force_detected">Brute Force</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select 
                    value={filters.severity} 
                    onValueChange={(v) => setFilters(f => ({...f, severity: v}))}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Severity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="info">Info</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by IP..."
                      value={filters.searchIP}
                      onChange={(e) => setFilters(f => ({...f, searchIP: e.target.value}))}
                      className="pl-9"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Events Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Event Type</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Endpoint</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEvents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                          No security events found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredEvents.map((event, idx) => (
                        <TableRow key={idx} className="hover:bg-gray-50">
                          <TableCell>{getEventIcon(event.event_type)}</TableCell>
                          <TableCell className="font-medium">
                            {event.event_type?.replace(/_/g, ' ')}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="link"
                              size="sm"
                              className="font-mono text-xs p-0 h-auto"
                              onClick={() => fetchIPDetails(event.ip_address)}
                            >
                              {event.ip_address}
                            </Button>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500 font-mono">
                            {event.endpoint}
                          </TableCell>
                          <TableCell>{getSeverityBadge(event.severity)}</TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {formatTime(event.timestamp)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="alerts" className="mt-4">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Alert</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Email Sent</TableHead>
                      <TableHead>Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                          No alerts in this period
                        </TableCell>
                      </TableRow>
                    ) : (
                      alerts.map((alert, idx) => (
                        <TableRow key={idx} className={alert.severity === 'critical' ? 'bg-red-50' : ''}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{alert.subject}</div>
                              <div className="text-sm text-gray-500">{alert.message}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="link"
                              size="sm"
                              className="font-mono text-xs p-0 h-auto"
                              onClick={() => fetchIPDetails(alert.ip_address)}
                            >
                              {alert.ip_address}
                            </Button>
                          </TableCell>
                          <TableCell>{getSeverityBadge(alert.severity)}</TableCell>
                          <TableCell>
                            {alert.email_sent ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700">Sent</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-gray-50">No</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {formatTime(alert.timestamp)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* IP Details Dialog */}
      <Dialog open={!!selectedIP} onOpenChange={() => { setSelectedIP(null); setIPDetails(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-mono">{selectedIP}</DialogTitle>
            <DialogDescription>
              Activity details for this IP address
            </DialogDescription>
          </DialogHeader>
          
          {ipDetails ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Total Events</p>
                    <p className="text-2xl font-bold">{ipDetails.total_events}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Period</p>
                    <p className="text-lg font-medium">{ipDetails.period_hours} hours</p>
                  </CardContent>
                </Card>
              </div>
              
              {ipDetails.event_breakdown && Object.keys(ipDetails.event_breakdown).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Event Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(ipDetails.event_breakdown).map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between">
                          <span className="text-sm">{type.replace(/_/g, ' ')}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {ipDetails.events?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Recent Events</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="max-h-60 overflow-y-auto">
                      <Table>
                        <TableBody>
                          {ipDetails.events.slice(0, 20).map((event, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="py-2">
                                <div className="flex items-center gap-2">
                                  {getEventIcon(event.event_type)}
                                  <span className="text-sm">{event.event_type?.replace(/_/g, ' ')}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-xs text-gray-500 py-2">
                                {formatTime(event.timestamp)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityAuditPage;
